import { forwardRef, type CSSProperties } from "react";
import FbkCardShell from "./FbkCardShell";
import { ThemeOverlay } from "./ThemeOverlay";
import { resolveCardTheme } from "./theme";
import {
  defaultFooterLeft,
  resolveTextStyle,
  type CardFormat,
  type FbkLeagueCardContent,
} from "./types";

interface FbkLeagueCardProps {
  format: CardFormat;
  content: FbkLeagueCardContent;
}

const FbkLeagueCard = forwardRef<HTMLDivElement, FbkLeagueCardProps>(
  function FbkLeagueCard({ format, content }, ref) {
    const isStory = format === "story";
    const style = content.style ?? {};
    const themeId = content.theme ?? "spotlight";
    const theme = resolveCardTheme(themeId, { monoColor: content.monoColor });

    const eyebrowStyle: CSSProperties = {
      ...resolveTextStyle(
        style.eyebrow,
        {
          fontFamily: theme.fontEyebrow,
          fontSizePx: 24,
          color: theme.accentColor,
          letterSpacing: "0.4em",
          bold: true,
        },
        format,
      ),
      textTransform: "uppercase",
      textAlign: "center",
      // Sit inside a gold-bordered chip so the eyebrow itself looks like a stamp.
      padding: isStory ? "10px 22px" : "8px 18px",
      borderRadius: 9999,
      border: `1.5px solid ${theme.accentColor}`,
      background: "rgba(0,0,0,0.35)",
      alignSelf: "center",
      whiteSpace: "nowrap",
      width: "fit-content",
      display: "inline-block",
      marginTop: isStory ? 24 : 16,
    };

    const headlineDefault = isStory ? 200 : 156;
    const headlineStyle: CSSProperties = {
      ...resolveTextStyle(
        style.headline,
        {
          fontFamily: theme.fontHeadline,
          fontSizePx: headlineDefault,
          color: theme.headlineColor,
          letterSpacing: "0.005em",
        },
        format,
      ),
      lineHeight: 0.92,
      textAlign: "center",
      whiteSpace: "pre-wrap",
      marginTop: isStory ? 36 : 24,
      // Hard gold glow behind the chant so it pulses off the screen.
      textShadow: `0 0 60px ${theme.accentColor}55, 0 0 120px ${theme.accentColor}30`,
    };

    const goldStyle: CSSProperties = {
      ...resolveTextStyle(
        style.goldHighlight,
        {
          fontFamily: theme.fontHeadline,
          fontSizePx: headlineDefault,
          color: theme.accentColor,
        },
        format,
      ),
      display: "block",
      lineHeight: 0.92,
      textShadow: `0 0 80px ${theme.accentColor}99, 0 0 160px ${theme.accentColor}55`,
    };

    const taglineStyle: CSSProperties = {
      ...resolveTextStyle(
        style.tagline,
        {
          fontFamily: theme.fontBody,
          fontSizePx: isStory ? 30 : 24,
          color: theme.subtleTextColor,
          italic: true,
        },
        format,
      ),
      lineHeight: 1.35,
      textAlign: "center",
      marginTop: isStory ? 22 : 14,
      maxWidth: isStory ? 820 : 760,
      marginLeft: "auto",
      marginRight: "auto",
    };

    return (
      <FbkCardShell
        ref={ref}
        format={format}
        footerLeft={content.footerLeft || defaultFooterLeft("FBK_LEAGUE")}
        footerRight={content.hashtags || "#WC2026 · #FBKLeague"}
        header={content.header}
        background={style.background}
        customBaseGradient={theme.customBaseGradient}
        headerOnLight={theme.headerOnLight}
        suppressBrandOverlays={theme.suppressBrandOverlays}
        hideFooter={content.footer?.hide}
      >
        <ThemeOverlay theme={theme} format={format} />

        {/* CONTENT COLUMN */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            position: "relative",
            zIndex: 2,
            minHeight: 0,
          }}
        >
          {/* Eyebrow chip — centered */}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div style={eyebrowStyle}>{content.eyebrow}</div>
          </div>

          {/* Stacked headline */}
          <div style={headlineStyle}>
            {content.headline}
            {content.goldHighlight && (
              <span style={goldStyle}>{content.goldHighlight}</span>
            )}
          </div>

          {content.tagline && <div style={taglineStyle}>{content.tagline}</div>}

          {/* PRIZE BLOCK — either mystery cover or revealed prize */}
          <div
            style={{
              marginTop: isStory ? 40 : 28,
              padding: isStory ? "32px 36px" : "22px 28px",
              borderRadius: 28,
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.55) 100%)",
              border: `2px solid ${theme.accentColor}`,
              boxShadow: `0 0 60px ${theme.accentColor}40, inset 0 0 40px ${theme.accentColor}15`,
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontFamily: theme.fontEyebrow,
                fontSize: isStory ? 22 : 18,
                color: theme.accentColor,
                letterSpacing: "0.4em",
                textTransform: "uppercase",
                fontWeight: 800,
                marginBottom: isStory ? 16 : 10,
              }}
            >
              {content.prizeTitle}
            </div>

            {content.mysteryMode ? (
              <>
                {/* Mystery cover — big question marks, reveal date, optional teaser */}
                <div
                  style={{
                    fontFamily: theme.fontHeadline,
                    fontSize: isStory ? 88 : 64,
                    color: theme.accentColor,
                    letterSpacing: "0.3em",
                    lineHeight: 1,
                    textShadow: `0 0 40px ${theme.accentColor}99`,
                    marginBottom: isStory ? 14 : 8,
                  }}
                >
                  ?? ?? ??
                </div>
                <div
                  style={{
                    fontFamily: theme.fontEyebrow,
                    fontSize: isStory ? 24 : 18,
                    color: "#FFFFFF",
                    letterSpacing: "0.3em",
                    textTransform: "uppercase",
                    fontWeight: 800,
                  }}
                >
                  Revealed {content.mysteryRevealAt || "soon"}
                </div>
                {content.mysteryTeaser && (
                  <div
                    style={{
                      marginTop: isStory ? 14 : 8,
                      fontFamily: theme.fontBody,
                      fontSize: isStory ? 22 : 18,
                      color: theme.subtleTextColor,
                      fontStyle: "italic",
                      opacity: 0.9,
                    }}
                  >
                    "{content.mysteryTeaser}"
                  </div>
                )}
              </>
            ) : (
              <div
                style={{
                  fontFamily: theme.fontHeadline,
                  fontSize: isStory ? 56 : 42,
                  color: "#FFFFFF",
                  lineHeight: 1.1,
                  letterSpacing: "0.01em",
                  whiteSpace: "pre-wrap",
                }}
              >
                {content.prizeText}
              </div>
            )}
          </div>

          {/* PLAYER COUNT — social proof strip */}
          {content.playerCountLabel && (
            <div
              style={{
                marginTop: isStory ? 24 : 16,
                fontFamily: theme.fontEyebrow,
                fontSize: isStory ? 20 : 16,
                color: theme.headlineColor,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                fontWeight: 800,
                textAlign: "center",
              }}
            >
              {content.playerCountLabel}
            </div>
          )}

          {/* JOIN CODE — big gold-bordered chip */}
          {content.joinCode && (
            <div
              style={{
                marginTop: isStory ? 28 : 18,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: isStory ? 16 : 12,
                  padding: isStory ? "18px 28px" : "14px 22px",
                  borderRadius: 18,
                  background: "rgba(0,0,0,0.45)",
                  border: `2px solid ${theme.accentColor}`,
                  boxShadow: `0 0 30px ${theme.accentColor}40`,
                }}
              >
                <span
                  style={{
                    fontFamily: theme.fontEyebrow,
                    fontSize: isStory ? 18 : 14,
                    color: theme.subtleTextColor,
                    letterSpacing: "0.3em",
                    textTransform: "uppercase",
                    fontWeight: 800,
                  }}
                >
                  Join code
                </span>
                <span
                  style={{
                    fontFamily: theme.fontHeadline,
                    fontSize: isStory ? 56 : 44,
                    color: theme.accentColor,
                    letterSpacing: "0.18em",
                    lineHeight: 1,
                    textShadow: `0 0 30px ${theme.accentColor}99`,
                  }}
                >
                  {content.joinCode}
                </span>
              </div>
            </div>
          )}

          {/* CTA pill */}
          {content.ctaText && (
            <div
              style={{
                marginTop: isStory ? 22 : 14,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  ...resolveTextStyle(
                    style.ctaText,
                    {
                      fontFamily: theme.fontBody,
                      fontSizePx: isStory ? 28 : 22,
                      color: theme.ctaFg,
                      bold: true,
                    },
                    format,
                  ),
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 12,
                  padding: isStory ? "22px 40px" : "16px 30px",
                  borderRadius: 9999,
                  background: theme.ctaBg,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  boxShadow: `0 24px 60px ${theme.ctaBg}55`,
                }}
              >
                {content.ctaText} →
              </div>
            </div>
          )}

          {content.smallPrint && (
            <div
              style={{
                marginTop: isStory ? 18 : 12,
                fontFamily: theme.fontBody,
                fontSize: isStory ? 18 : 15,
                color: theme.subtleTextColor,
                textAlign: "center",
                opacity: 0.85,
              }}
            >
              {content.smallPrint}
            </div>
          )}
        </div>
      </FbkCardShell>
    );
  },
);

export default FbkLeagueCard;
